import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; 
import NavBar from '../components/NavBar';
import Spinner from '../components/Spinner';
import './../styles.css';
import skillsList from '../skills.json';

const Applications = () => {
  const [applications, setApplications] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newApplication, setNewApplication] = useState({
    companyName: '',
    role: '',
    skills: [],
    notes: '',
    link: '',
    submittedOn: '',
    status: 'select',
  });
  const [emptyFieldsError, setEmptyFieldsError] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [filteredSkills, setFilteredSkills] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [showNoOptions, setShowNoOptions] = useState(false);
  const [currentDate, setCurrentDate] = useState('');
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('userId');
  const navigate = useNavigate();
  let url;

  useEffect(() => {
    fetchApplications();
    setCurrentDate(new Date().toISOString().split('T')[0]);
  }, []);

  const fetchApplications = async () => {
    try {
      url = `${process.env.REACT_APP_SRV_BASE_URL}/api/applications/${userId}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setApplications(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching applications:', error);
      setApplications([]);
    }
  };

  // =========================================
  //      CRUD functionalities starts here
  // =========================================
  const sendDataToBackend = async (data) => {
     // Include userId in the data payload
  const payload = { ...data, userId };

  try {
    const response = await fetch(`${process.env.REACT_APP_SRV_BASE_URL}/api/applications`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload),
    });
    const responseData = await response.json();
    if (response.ok) {
      fetchApplications();
    } else {
      console.error('Error in backend response:', responseData);
    }
  } catch (error) {
    console.error('Error sending data to backend:', error);
  }
  };

  const updateApplicationInBackend = async (applicationId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SRV_BASE_URL}/api/applications/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          id: applicationId._id,
          data: applicationId
        }),
      });
  
      if (response.ok) {
        fetchApplications();
      } else {
        const responseData = await response.json();
        console.error('Error in backend response:', responseData);
      }
    } catch (error) {
      console.error('Error updating application:', error);
    }
  };
  

  const handleAddApplication = (e) => {
    e.preventDefault();

    // Check if the status field is empty and focus on it
    if (newApplication.status === 'select') {
      const statusInput = document.querySelector('select[name="status"]');
      if (statusInput) statusInput.focus();
      setEmptyFieldsError(true);
      return;
    }

    if (isUpdateMode) {
      // Update existing application
      const updatedApplications = applications.map((app) =>
        app._id === selectedApplication._id ? newApplication : app
      );
      setApplications(updatedApplications);
      updateApplicationInBackend(newApplication);
      setShowModal(false);
      resetForm();
      setNewApplication({
        companyName: '',
        role: '',
        skills: [],
        notes: '',
        link: '',
        submittedOn: '',
        status: 'select',
      });
      setEmptyFieldsError(false);
      setIsUpdateMode(false);
      setSelectedApplication(null);
    } else {
      // Add new application
      setApplications([...applications, newApplication]);
      sendDataToBackend(newApplication);
      setShowModal(false);
      resetForm();
      setNewApplication({
        companyName: '',
        role: '',
        skills: [],
        notes: '',
        link: '',
        submittedOn: '',
        status: 'select',
      });
      setEmptyFieldsError(false);
    }
  };

  const deleteApplicationFromBackend = async (applicationId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SRV_BASE_URL}/api/applications/`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          id: applicationId
        }),
      });
  
      if (response.ok) {
        fetchApplications();
      } else {
        const responseData = await response.json();
        console.error('Error in backend response:', responseData);
      }
    } catch (error) {
      console.error('Error deleting application:', error);
    }
  };

  const handleDeleteApplication = (id) => {
    // Delete application from frontend and backend
    const updatedApplications = applications.filter((app) => app._id !== id);
    setApplications(updatedApplications);
    // Send delete request to backend
    deleteApplicationFromBackend(id);
  };

  const handleUpdateApplication = (application) => {
    setShowModal(true);
    setIsUpdateMode(true);
    setSelectedApplication(application);
    setNewApplication(application);
  };

  // =========================================
  //      CRUD functionalities ends here
  // =========================================

  // Function to reset the form fields
  const resetForm = () => {
    setNewApplication({
      companyName: '',
      role: '',
      skills: [],
      notes: '',
      link: '',
      submittedOn: '',
      status: 'select',
    });
    setEmptyFieldsError(false);
    setIsUpdateMode(false);
    setSelectedApplication(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewApplication({ ...newApplication, [name]: value });
  };

  const handleSkillSearch = (e) => {
    const keyword = e.target.value.toLowerCase();
    setSearchInput(keyword);

    if (keyword === '') {
      setFilteredSkills([]);
      setShowNoOptions(false);
      return;
    }
    const filtered = skillsList.filter(skill =>
      skill.toLowerCase().includes(keyword)
    );
    setFilteredSkills(filtered);

    if (filtered.length === 0 && keyword !== '') {
      setShowNoOptions(true);
    } else {
      setShowNoOptions(false);
    }
  };

  const handleAddSkill = (skill) => {
    setNewApplication({ ...newApplication, skills: [...newApplication.skills, skill] });
    setSearchInput('');
    setFilteredSkills([]);
  };

  const handleDeleteSkill = (skill) => {
    const updatedSkills = newApplication.skills.filter(s => s !== skill);
    setNewApplication({ ...newApplication, skills: updatedSkills });
  };

  const handleInsightsClick = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      navigate('/insights');
    }, 200);
  };

  return (
    <div>
      <NavBar />
      <div className="center">
        <button className="btn btn-primary mt-3 btn-add-application" onClick={() => { resetForm(); setShowModal(true); }}>
          Add Application
        </button>
      </div>

      {applications.length > 0 && (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Company</th>
                <th>Role</th>
                <th>Skills</th>
                <th>Notes</th>
                <th>Application Link</th>
                <th>Submitted On</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {applications.map((application) => (
                <tr key={application._id}>
                  <td>{application.companyName}</td>
                  <td>{application.role}</td>
                  <td>
                  <div className="selected-skills-container">
                      {application.skills.map(skill => (
                        <span key={skill} className="selected-skill-table">
                          {skill}
                        </span>
                      ))}
                  </div>
                  </td>
                  <td>{application.notes}</td>
                  <td>{application.link}</td>
                  <td>{application.submittedOn}</td>
                  <td>{application.status}</td>
                  <td>
                    <button className="btn btn-update ml-1" onClick={() => handleUpdateApplication(application)}>
                      Update
                    </button>
                    <button className="btn btn-danger" onClick={() => handleDeleteApplication(application._id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {applications.length > 0 && (
        <div className="insights-container">
          {!loading && (
            <span className="insights-text" onClick={handleInsightsClick}>
              GAIN YOUR INSIGHTS
            </span>
          )}
          {loading && <Spinner />}
        </div>
      )}

      {/* Add Application Modal */}
      {showModal && (
        <div className="modal">
          <div className="modal-content">
            <span className="close" onClick={() => { resetForm(); setShowModal(false); }}>
              &times;
            </span>
            <h2>{isUpdateMode ? 'Update Application' : 'Add Application'}</h2>
            <form onSubmit={handleAddApplication}>
              <div>
                <label>Company:</label>
                <input type="text" name="companyName" value={newApplication.companyName} onChange={handleInputChange} required />
              </div>
              <div>
                <label>Role:</label>
                <input type="text" name="role" value={newApplication.role} onChange={handleInputChange} required />
              </div>
              <div>
                <label>Skills:</label>
                <div className="skills-search-container">
                  <input type="text" value={searchInput} onChange={handleSkillSearch} placeholder="Add skills" />
                  {showNoOptions && <p className="no-options">No options available</p>}
                  {filteredSkills.length > 0 && (
                    <ul className="skills-list">
                      {filteredSkills.map((skill) => (
                        <li key={skill} onClick={() => handleAddSkill(skill)}>
                          {skill}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {newApplication.skills.map(skill => (
                  <span key={skill} className="selected-skill">
                    {skill}
                    <button className="remove-skill" onClick={() => handleDeleteSkill(skill)}>x</button>
                  </span>
                ))}
              </div>
              <div>
                <label>Notes:</label>
                <input type="text" name="notes" value={newApplication.notes} onChange={handleInputChange} />
              </div>
              <div>
                <label>Application Link:</label>
                <input type="text" name="link" value={newApplication.link} onChange={handleInputChange} />
              </div>
              <div>
                <label>Submitted On:</label>
                <input type="date" name="submittedOn" value={newApplication.submittedOn} onChange={handleInputChange} />
              </div>
              <div>
                <label>Status:</label>
                <select name="status" value={newApplication.status} onChange={handleInputChange} required>
                  <option value="select">Select</option>
                  <option value="interested">Interested</option>
                  <option value="applied">Applied</option>
                  <option value="interviewing">Interviewing</option>
                  <option value="accepted">Accepted</option>
                </select>
              </div>
              {emptyFieldsError && <p className="error-message">Please fill out all fields</p>}
              <div className="center">
                <button type="submit" className="btn btn-primary mt-1">
                  {isUpdateMode ? 'Update' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default Applications;
